import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  CircularProgress, Stack, Chip, alpha,
  Autocomplete,
} from '@mui/material';
import CloseIcon         from '@mui/icons-material/Close';
import SaveIcon          from '@mui/icons-material/Save';
import AddIcon           from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AutoAwesomeIcon   from '@mui/icons-material/AutoAwesome';
import TuneIcon          from '@mui/icons-material/Tune';
import LockOutlinedIcon  from '@mui/icons-material/LockOutlined';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import AccessTimeIcon    from '@mui/icons-material/AccessTime';
import { fetchRecords }  from '../services/api';
import { TABLE_CONFIG, generatePK } from '../config/fieldConfig';
import { useApp } from '../context/AppContext';

// ── Palette ───────────────────────────────────────────────────────────────────
const GREEN   = '#22c55e';
const GREEN_D = '#16a34a';
const GREEN_L = '#f0fdf4';
const GREEN_B = '#bbf7d0';

// ── Date helpers ──────────────────────────────────────────────────────────────
const getLastDay = (year, month) => new Date(year, month, 0).getDate();

const toDateStr = (year, month, day) =>
  `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;

const getFiscalYear = (month, year) => {
  if (month >= 4) return `${year}-${String(year + 1).slice(2)}`;
  return `${year - 1}-${String(year).slice(2)}`;
};

const getPeriodName = (month, year) => {
  const M = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${M[month - 1]}-${year}`;
};

/** Period start/end for a payroll month (matches backend generatePayPeriods). */
const calcPeriodBounds = (year, month, payroll) => {
  const pad = n => String(n).padStart(2, '0');
  const cutoffDay = Number(payroll?.cutoff_day) || 0;
  const thisLastDay = getLastDay(year, month);

  if (cutoffDay > 0 && cutoffDay < thisLastDay) {
    let prevMonth = month - 1;
    let prevYear = year;
    if (prevMonth === 0) { prevMonth = 12; prevYear--; }
    const prevLastDay = getLastDay(prevYear, prevMonth);
    const startDay = Math.min(cutoffDay + 1, prevLastDay);
    return {
      start_date: `${prevYear}-${pad(prevMonth)}-${pad(startDay)}`,
      end_date: `${year}-${pad(month)}-${pad(cutoffDay)}`,
      anchorMonth: month,
      anchorYear: year,
    };
  }

  return {
    start_date: `${year}-${pad(month)}-01`,
    end_date: `${year}-${pad(month)}-${pad(thisLastDay)}`,
    anchorMonth: month,
    anchorYear: year,
  };
};

/** Next suggested start date + period name after latest period for this payroll (or current month). */
const suggestPeriodFromPayroll = (payroll, existingRecords, excludeRecordId) => {
  if (!payroll?.payroll_id) return null;

  const periods = (existingRecords || [])
    .filter(p =>
      p.payroll_id === payroll.payroll_id &&
      (excludeRecordId == null || (p.id ?? p._id) !== excludeRecordId)
    )
    .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));

  let year;
  let month;
  if (periods.length > 0) {
    const anchor = periods[0].end_date || periods[0].start_date;
    const d = new Date(anchor);
    month = d.getMonth() + 2;
    year = d.getFullYear();
    if (month > 12) { month = 1; year++; }
  } else {
    const now = new Date();
    year = now.getFullYear();
    month = now.getMonth() + 1;
  }

  const bounds = calcPeriodBounds(year, month, payroll);
  return {
    start_date: bounds.start_date,
    period_name: payroll.payroll_name ? `${payroll.payroll_name} - ${getPeriodName(bounds.anchorMonth, bounds.anchorYear)}` : getPeriodName(bounds.anchorMonth, bounds.anchorYear),
  };
};

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_META = {
  pending:   { color: '#D97706', bg: '#fffbeb', border: '#fde68a',  label: 'Pending'   },
  processed: { color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe',  label: 'Processed' },
  paid:      { color: GREEN,     bg: GREEN_L,   border: GREEN_B,    label: 'Paid'      },
};

// ── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon, label, color = GREEN }) => (
  <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
    <Box sx={{
      width: 30, height: 30, borderRadius: '8px',
      bgcolor: alpha(color, 0.12),
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
    </Box>
    <Typography fontSize={12.5} fontWeight={700} color="text.secondary" letterSpacing={0.6}
      sx={{ textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EDF5' }} />
  </Stack>
);

// ── Auto Badge ────────────────────────────────────────────────────────────────
const AutoBadge = () => (
  <Chip
    icon={<AutoAwesomeIcon sx={{ fontSize: '10px !important' }} />}
    label="Auto"
    size="small"
    sx={{
      height: 18, fontSize: 9.5, fontWeight: 700,
      bgcolor: alpha(GREEN, 0.1), color: GREEN,
      '& .MuiChip-icon': { color: GREEN },
      ml: 0.8,
    }}
  />
);

const ReadOnlyField = ({ label, value, icon, xs = 12, sm = 4 }) => (
  <Grid item xs={xs} sm={sm}>
    <TextField
      size="small" fullWidth
      label={<Stack direction="row" alignItems="center">{label} <AutoBadge /></Stack>}
      value={value || ''}
      InputProps={{
        readOnly: true,
        startAdornment: icon
          ? React.cloneElement(icon, { sx: { fontSize: 13, color: '#94A3B8', mr: 0.5 } })
          : null,
      }}
      InputLabelProps={{ shrink: true }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '10px', bgcolor: '#F8FAFC',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN },
        },
        '& label.Mui-focused': { color: GREEN },
      }}
    />
  </Grid>
);

// ── Empty state ───────────────────────────────────────────────────────────────
const EMPTY = {
  COMPANY_id:  '',
  payroll_id:  '',
  period_name: '',
  fiscal_year: '',
  start_date:  '',
  end_date:    '',
  payment_date:'',
  status:      'pending',
  created_at:  '',
  updated_at:  '',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN },
  },
  '& label.Mui-focused': { color: GREEN },
};

const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const formatDateShort = (dateStr) => {
  const M = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = M[d.getMonth()];
  return `${day} ${month}`;
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function PayPeriodForm({ open, onClose, onSave, record, existingRecords = [] }) {
  const { state: appState } = useApp();
  const isEdit = !!record;
  const [form,       setForm]       = useState(EMPTY);
  const [errors,     setErrors]     = useState({});
  const [saving,     setSaving]     = useState(false);
  const [companies,  setCompanies]  = useState([]);
  const [payrolls,   setPayrolls]   = useState([]);
  const [loadingFKs, setLoadingFKs] = useState(false);
  const [isPeriodNameEdited, setIsPeriodNameEdited] = useState(false);

  // Load FKs once
  useEffect(() => {
    if (!open) return;
    setLoadingFKs(true);
    Promise.all([
      fetchRecords('COMPANY'),
      fetchRecords('PAYROLL')
    ])
      .then(([cRes, pRes]) => {
        setCompanies(cRes.data?.data || []);
        setPayrolls(pRes.data?.data || []);
      })
      .catch(() => {})
      .finally(() => setLoadingFKs(false));
  }, [open]);

  // Populate on open
  useEffect(() => {
    if (open) {
      if (record) {
         setForm({ ...EMPTY, ...record });
         setIsPeriodNameEdited(true);
      } else {
         const activeCompId = appState?.activeContext?.company_id || '';
         setForm({ ...EMPTY, COMPANY_id: activeCompId, created_at: new Date().toISOString().split('T')[0] });
         setIsPeriodNameEdited(false);
      }
      setErrors({});
    }
  }, [open, record, appState]);

  // Auto-calculate derived dates when start_date or payroll changes (period name stays editable)
  useEffect(() => {
    const sd = form.start_date;
    if (!sd) return;

    const payroll = payrolls.find(p => p.payroll_id === form.payroll_id);
    const d = new Date(sd);
    const calYear = d.getFullYear();
    const calMonth = d.getMonth() + 1;

    let endDate;
    let finalPeriodName;
    let finalPaymentDate;

    const normalizedPeriodType = String(payroll?.period_type || '').toLowerCase().replace(/[\s-]/g, '_');
    const isWeeklyish = normalizedPeriodType === 'weekly' || normalizedPeriodType === 'biweekly' || normalizedPeriodType === 'bi_weekly';

    if (isWeeklyish) {
      const d1 = new Date(sd);
      const daysToAdd = normalizedPeriodType === 'weekly' ? 6 : 13;
      const d2 = new Date(d1);
      d2.setDate(d1.getDate() + daysToAdd);
      endDate = toDateStr(d2.getFullYear(), d2.getMonth() + 1, d2.getDate());
      
      const isoWeek = getISOWeek(d1);
      const startFormatted = formatDateShort(sd);
      const endFormatted = formatDateShort(endDate) + ' ' + d2.getFullYear();
      finalPeriodName = payroll?.payroll_name ? `${payroll.payroll_name} - WK${isoWeek} (${startFormatted} - ${endFormatted})` : `WK${isoWeek} (${startFormatted} - ${endFormatted})`;

      const offset = Number(payroll?.payment_date_offset || 0);
      const pDate = new Date(d2);
      pDate.setDate(d2.getDate() + offset);
      finalPaymentDate = toDateStr(pDate.getFullYear(), pDate.getMonth() + 1, pDate.getDate());
    } else {
      let anchorMonth = calMonth;
      let anchorYear = calYear;
      endDate = toDateStr(calYear, calMonth, getLastDay(calYear, calMonth));

      if (payroll) {
        let matched = false;
        for (let m = 1; m <= 12; m++) {
          const bounds = calcPeriodBounds(calYear, m, payroll);
          if (bounds.start_date === sd) {
            anchorMonth = bounds.anchorMonth;
            anchorYear = bounds.anchorYear;
            endDate = bounds.end_date;
            matched = true;
            break;
          }
        }
        if (!matched) {
          const bounds = calcPeriodBounds(calYear, calMonth, payroll);
          anchorMonth = bounds.anchorMonth;
          anchorYear = bounds.anchorYear;
          endDate = bounds.end_date;
        }
      }

      let payMonth = anchorMonth;
      let payYear = anchorYear;
      if (payroll?.payment_month_type === 'next_month') {
        payMonth += 1;
        if (payMonth > 12) {
          payMonth = 1;
          payYear += 1;
        }
      }
      const payLastDay = getLastDay(payYear, payMonth);
      const payDay = Math.min(Number(payroll?.payment_day || payLastDay), payLastDay);

      finalPeriodName = payroll?.payroll_name ? `${payroll.payroll_name} - ${getPeriodName(anchorMonth, anchorYear)}` : getPeriodName(anchorMonth, anchorYear);
      finalPaymentDate = toDateStr(payYear, payMonth, payDay);
    }

    const d1 = new Date(sd);
    const d2 = new Date(endDate);
    const totalDays = Math.round((d2 - d1) / 86400000) + 1;
    let totalWorkingDays = 0;
    
    if (payroll?.salary_calculation_type === 'actual_days') {
      totalWorkingDays = totalDays;
    } else if (payroll?.working_days && Array.isArray(payroll.working_days) && payroll.working_days.length > 0) {
      const dayMap = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
      const workingIndexes = payroll.working_days.map(d => dayMap[d?.toUpperCase()]).filter(i => i !== undefined);
      for (let day = new Date(d1); day <= d2; day.setDate(day.getDate() + 1)) {
        if (workingIndexes.includes(day.getDay())) totalWorkingDays++;
      }
    } else {
      let totalSundays = 0;
      for (let day = new Date(d1); day <= d2; day.setDate(day.getDate() + 1)) {
        if (day.getDay() === 0) totalSundays++;
      }
      totalWorkingDays = totalDays - totalSundays;
    }
    const totalOffDays = totalDays - totalWorkingDays;

    setForm(prev => ({
      ...prev,
      period_name:  isPeriodNameEdited ? prev.period_name : finalPeriodName,
      fiscal_year:  getFiscalYear(calMonth, calYear),
      end_date:     endDate,
      payment_date: finalPaymentDate,
      total_calendar_days: totalDays,
      total_off_days: totalOffDays,
      total_working_days: totalWorkingDays,
      updated_at:   new Date().toISOString().split('T')[0],
    }));
  }, [form.start_date, form.payroll_id, payrolls, isPeriodNameEdited]);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePayrollChange = (payrollId) => {
    const payroll = payrolls.find(p => p.payroll_id === payrollId);
    const excludeId = isEdit ? (record?.id ?? record?._id) : null;

    setForm(prev => {
      const next = { ...prev, payroll_id: payrollId };
      if (!payrollId || !payroll) return next;

      const suggestion = suggestPeriodFromPayroll(payroll, existingRecords, excludeId);
      if (suggestion) {
        next.start_date = suggestion.start_date;
        if (!isPeriodNameEdited) {
          next.period_name = suggestion.period_name;
        }
      }
      return next;
    });

    if (errors.payroll_id) setErrors(prev => ({ ...prev, payroll_id: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.COMPANY_id) e.COMPANY_id = 'Company is required';
    if (!form.payroll_id) e.payroll_id = 'Payroll is required';
    if (!form.start_date) e.start_date = 'Start date is required';
    if (!form.period_name) e.period_name = 'Period name is required';
    else {
      const cfg = TABLE_CONFIG?.PAY_PERIOD?.fields?.period_name;
      if (cfg?.pattern && !new RegExp(cfg.pattern).test(form.period_name)) {
        e.period_name = cfg.patternMsg || 'Invalid format';
      }
    }
    const dup = existingRecords.some(r =>
      (r.COMPANY_id === form.COMPANY_id || r.company_id === form.COMPANY_id) &&
      r.payroll_id === form.payroll_id &&
      r.period_name?.toLowerCase() === form.period_name?.toLowerCase() &&
      (!isEdit || (r.id ?? r._id) !== (record?.id ?? record?._id))
    );
    if (dup) e.period_name = `${form.period_name} already exists for this company`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (andNew = false) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.total_calendar_days;
      delete payload.total_off_days;
      delete payload.total_working_days;

      if (!isEdit && !payload.period_id) {
        const selectedPayroll = payrolls.find(p => p.payroll_id === payload.payroll_id);
        const compId = selectedPayroll?.COMPANY_id || selectedPayroll?.company_id || '';
        payload.period_id = generatePK(TABLE_CONFIG.PAY_PERIOD, existingRecords, compId);
      }

      await onSave(payload, andNew);
      if (andNew) { setForm({ ...EMPTY, created_at: new Date().toISOString().split('T')[0] }); setErrors({}); }
    } catch (err) {
      const status = err?.status || err?.response?.status;
      const meta = err?.meta || err?.response?.data?.meta;
      const message = err?.userMessage || err?.response?.data?.message || err?.message;
      const fieldErrors = {};      
      if (status === 409) {
        if (meta?.target) {
          const targets = Array.isArray(meta.target) ? meta.target : [meta.target];
          if (targets.includes('period_name')) {
            fieldErrors.period_name = message || 'Period name already exists for this payroll';
          }
        } else if (message && message.toLowerCase().includes('already exists')) {
          fieldErrors.period_name = message;
        }
      }
      if (Object.keys(fieldErrors).length) {
        setErrors(prev => ({ ...prev, ...fieldErrors }));
      }
    } finally {
      setSaving(false);
    }
  };

  const companyOpts = companies.map(r => ({
    value: r.company_id,
    label: `${r.company_name} (${r.company_id})`,
    raw: r,
  }));

  const filteredPayrolls = payrolls.filter(p => !form.COMPANY_id || p.COMPANY_id === form.COMPANY_id || p.company_id === form.COMPANY_id);
  const payrollOpts = filteredPayrolls.map(r => ({
    value: r.payroll_id,
    label: `${r.payroll_name || r.payroll_id}  (${r.period_type || ''} · cutoff ${r.cutoff_day} · pay ${r.payment_day})`,
    raw: r,
  }));

  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.18)' } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${GREEN_D} 0%, ${GREEN} 100%)`,
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CalendarMonthIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>
            <Box>
              <Typography fontWeight={800} fontSize={16} color="white" lineHeight={1.2}>
                {isEdit ? 'Edit Pay Period' : 'New Pay Period'}
              </Typography>
              <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.75)' }}>
                {isEdit ? `Editing ${record?.period_name} · ${record?.fiscal_year}` : 'Pick a payroll + start date — everything auto-fills'}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small"
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#F8FAFC' }}>
        {loadingFKs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: GREEN }} />
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>

            {/* ── Section 1: Payroll + Start Date ── */}
            <Box sx={{ mb: 3, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
              <SectionHeader icon={<TuneIcon />} label="Period Setup" />
              <Grid container spacing={2}>
                {/* Company Selection */}
                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    options={companyOpts}
                    value={companyOpts.find(o => o.value === form.COMPANY_id) || null}
                    onChange={(_, v) => {
                       setForm(prev => ({ ...prev, COMPANY_id: v?.value || '', payroll_id: '' }));
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Company *"
                        error={!!errors.COMPANY_id}
                        helperText={errors.COMPANY_id}
                        sx={inputSx}
                      />
                    )}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                  />
                </Grid>

                {/* Payroll FK */}
                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    options={payrollOpts}
                    value={payrollOpts.find(o => o.value === form.payroll_id) || null}
                    onChange={(_, v) => handlePayrollChange(v?.value || '')}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Payroll *"
                        error={!!errors.payroll_id}
                        helperText={errors.payroll_id || 'Start date and period name are suggested from the payroll (you can still edit them)'}
                        sx={inputSx}
                      />
                    )}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                  />
                </Grid>

                {/* Start Date — triggers all auto-calc */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small" fullWidth type="date"
                    label="Start Date *"
                    value={form.start_date}
                    onChange={e => set('start_date', e.target.value)}
                    error={!!errors.start_date}
                    helperText={errors.start_date || 'Auto-filled from payroll — editable'}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>

                {/* Period Name — auto */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small" fullWidth
                    label={<Stack direction="row" alignItems="center">Period Name <AutoBadge /></Stack>}
                    value={form.period_name}
                    onChange={e => { set('period_name', e.target.value); setIsPeriodNameEdited(true); }}
                    error={!!errors.period_name}
                    helperText={errors.period_name || 'Auto-filled from payroll — editable (e.g. APR-2026)'}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* ── Section 2: Auto-calculated dates ── */}
            <Box sx={{ mb: 3, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
              <SectionHeader icon={<CalendarMonthIcon />} label="Dates" />
              <Grid container spacing={2}>
                <ReadOnlyField label="End Date"     value={form.end_date}     icon={<LockOutlinedIcon />} sm={4} />
                <ReadOnlyField label="Payment Date" value={form.payment_date} icon={<LockOutlinedIcon />} sm={4} />
                <ReadOnlyField label="Fiscal Year"  value={form.fiscal_year}  icon={<LockOutlinedIcon />} sm={4} />
              </Grid>
              <Typography fontSize={11} color="text.secondary" mt={1.5} mb={2}>
                End date = last day of month · Payment date = payroll's payment day · Fiscal year = Indian FY (Apr–Mar)
              </Typography>
              
              <SectionHeader icon={<AccessTimeIcon />} label="Working Days Calculation" color="#8b5cf6" />
              <Grid container spacing={2}>
                <ReadOnlyField label="Total Calendar Days" value={form.total_calendar_days} xs={4} sm={4} />
                <ReadOnlyField label="Total Off Days"      value={form.total_off_days}      xs={4} sm={4} />
                <ReadOnlyField label="Total Working Days"  value={form.total_working_days}  xs={4} sm={4} />
              </Grid>
            </Box>

            {/* ── Section 3: Status ── */}
            <Box sx={{ mb: 3, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
              <SectionHeader icon={<CheckCircleIcon />} label="Status" />
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {Object.entries(STATUS_META).map(([s, meta]) => (
                  <Chip
                    key={s}
                    label={meta.label}
                    onClick={() => set('status', s)}
                    sx={{
                      fontWeight: 700, fontSize: 12.5, borderRadius: '10px',
                      cursor: 'pointer', transition: 'all 0.15s',
                      px: 0.5,
                      ...(form.status === s ? {
                        bgcolor: meta.bg,
                        color: meta.color,
                        border: `1.5px solid ${meta.border}`,
                        boxShadow: `0 2px 8px ${alpha(meta.color, 0.25)}`,
                      } : {
                        bgcolor: 'transparent',
                        color: '#94A3B8',
                        border: '1.5px solid #E2E8F0',
                        '&:hover': { borderColor: meta.color, color: meta.color },
                      }),
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* ── Section 4: Timestamps (read-only) ── */}
            <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
              <SectionHeader icon={<AccessTimeIcon />} label="Timestamps" color="#94A3B8" />
              <Grid container spacing={2}>
                <ReadOnlyField
                  label="Created At"
                  value={form.created_at || today}
                  icon={<LockOutlinedIcon />}
                  sm={6}
                />
                <ReadOnlyField
                  label="Updated At"
                  value={form.updated_at || today}
                  icon={<LockOutlinedIcon />}
                  sm={6}
                />
              </Grid>
            </Box>

          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1.5px solid #E8EDF5', gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '10px', fontWeight: 600, fontSize: 13,
            borderColor: '#E2E8F0', color: '#64748B', mr: 'auto',
            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={() => handleSubmit(true)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: GREEN }} /> : <AddIcon />}
          variant="outlined"
          sx={{
            borderRadius: '10px', fontWeight: 700, fontSize: 13,
            borderColor: GREEN, color: GREEN,
            '&:hover': { bgcolor: GREEN_L, borderColor: GREEN_D },
          }}
        >
          {saving ? 'Saving…' : 'Save & Add New'}
        </Button>

        <Button
          onClick={() => handleSubmit(false)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />}
          variant="contained"
          sx={{
            borderRadius: '10px', fontWeight: 700, fontSize: 13,
            background: `linear-gradient(135deg, ${GREEN_D}, ${GREEN})`,
            boxShadow: `0 4px 14px ${alpha(GREEN, 0.4)}`,
            '&:hover': { background: `linear-gradient(135deg, #15803d, ${GREEN_D})` },
          }}
        >
          {saving ? 'Saving…' : (isEdit ? 'Update' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
